import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { X, AlertCircle } from 'lucide-react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Product, PRODUCT_TYPES, CollectionType, ProductCategory } from '../../types';
import { ImageGallery } from '../../components/admin/ImageGallery';
import { RichTextEditor } from '../../components/admin/RichTextEditor';

interface EditProductDialogProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product;
  onSave: (updatedProduct: Product) => void;
}

const COLLECTION_TYPES: CollectionType[] = ['Feature Item', 'On Sale'];
const PRODUCT_CATEGORIES: ProductCategory[] = ['New', 'Used', 'Like New'];

const productSchema = Yup.object().shape({
  name: Yup.string().required('Product name is required'),
  brand: Yup.string().required('Brand is required'),
  type: Yup.string().oneOf([...PRODUCT_TYPES], 'Invalid product type').required('Type is required'),
  collectionType: Yup.string().oneOf(COLLECTION_TYPES, 'Invalid collection type').required('Collection type is required'),
  category: Yup.string().oneOf(PRODUCT_CATEGORIES, 'Invalid category').required('Category is required'),
  price: Yup.number().positive('Price must be positive').required('Price is required'),
  description: Yup.string().required('Description is required'),
  stockLevel: Yup.number().min(0, 'Stock cannot be negative').required('Stock level is required'),
  images: Yup.array().min(1, 'At least one image is required')
});

export function EditProductDialog({ isOpen, onClose, product, onSave }: EditProductDialogProps) {
  const [showUnsavedChanges, setShowUnsavedChanges] = useState(false);
  const [formValues, setFormValues] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleClose = () => {
    if (formValues && !isSaving) {
      setShowUnsavedChanges(true);
    } else {
      onClose();
    }
  };

  const handleSaveViaServer = async (values: any) => {
    try {
      setIsSaving(true);

      console.log('💾 Saving product via server route:', {
        id: product.id,
        updates: values,
        timestamp: new Date().toISOString()
      });

      // Call server route with service role
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-product`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            id: product.id,
            updates: {
              name: values.name,
              brand: values.brand,
              type: values.type,
              product_type: values.collectionType,
              category: values.category,
              price: Number(values.price),
              description: values.description,
              stock_level: Number(values.stockLevel),
              images: values.images,
              is_published: product.is_published, // Keep current publish status
              is_draft: product.is_draft // Keep current draft status
            }
          })
        }
      );

      console.log('📡 Server response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('❌ Server update failed:', errorData);
        throw new Error(errorData.error || `Update failed with status ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Server update successful:', result);

      if (!result.success || !result.data) {
        throw new Error('Invalid response from server');
      }

      // Create updated product object with server response data
      const updatedProduct: Product = {
        ...product,
        ...result.data,
        stockLevel: result.data.stock_level,
        productType: result.data.product_type,
        collectionType: result.data.product_type
      };

      console.log('🔄 Calling onSave with updated product:', updatedProduct);

      // Call the parent's save handler with the updated product
      onSave(updatedProduct);

      // Close the dialog
      setFormValues(null);
      onClose();

    } catch (error) {
      console.error('💥 Error saving product via server:', error);
      throw error; // Re-throw to be handled by Formik
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Dialog.Root open={isOpen} onOpenChange={handleClose}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          <Dialog.Content className="fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] w-[95vw] md:w-full md:max-w-2xl bg-white rounded-lg shadow-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <Dialog.Title className="text-xl font-semibold text-gray-900">
                  Edit Product
                </Dialog.Title>
                <Dialog.Close className="text-gray-400 hover:text-gray-500">
                  <X className="w-5 h-5" />
                </Dialog.Close>
              </div>

              <Formik
                initialValues={{
                  name: product.name,
                  brand: product.brand,
                  type: product.type,
                  collectionType: product.collectionType || product.product_type,
                  category: product.category,
                  price: product.price,
                  description: product.description,
                  stockLevel: product.stockLevel || product.stock_level,
                  images: product.images || []
                }}
                validationSchema={productSchema}
                onSubmit={handleSaveViaServer}
              >
                {({ errors, touched, setFieldValue, values, dirty, isSubmitting, setStatus, status }) => {
                  // Update formValues when form becomes dirty
                  if (dirty && !formValues) {
                    setFormValues(values);
                  }
                  
                  return (
                    <Form className="space-y-6">
                      <div>
                        <ImageGallery
                          images={values.images}
                          onImagesChange={(newImages) => setFieldValue('images', newImages)}
                          productId={product.id} // Pass product ID for secure server upload
                        />
                        {errors.images && touched.images && (
                          <div className="mt-1 text-sm text-red-600 flex items-center">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            {errors.images}
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-1 gap-6">
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                            Product Name *
                          </label>
                          <Field
                            id="name"
                            name="name"
                            type="text"
                            className={`mt-1 block w-full rounded-md shadow-sm ${
                              errors.name && touched.name
                                ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                            }`}
                          />
                          {errors.name && touched.name && (
                            <div className="mt-1 text-sm text-red-600 flex items-center">
                              <AlertCircle className="w-4 h-4 mr-1" />
                              {errors.name}
                            </div>
                          )}
                        </div>

                        <div>
                          <label htmlFor="brand" className="block text-sm font-medium text-gray-700">
                            Brand *
                          </label>
                          <Field
                            id="brand"
                            name="brand"
                            type="text"
                            className={`mt-1 block w-full rounded-md shadow-sm ${
                              errors.brand && touched.brand
                                ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                            }`}
                          />
                          {errors.brand && touched.brand && (
                            <div className="mt-1 text-sm text-red-600 flex items-center">
                              <AlertCircle className="w-4 h-4 mr-1" />
                              {errors.brand}
                            </div>
                          )}
                        </div>

                        <div>
                          <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                            Type *
                          </label>
                          <Field
                            as="select"
                            id="type"
                            name="type"
                            className={`mt-1 block w-full rounded-md shadow-sm ${
                              errors.type && touched.type
                                ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                            }`}
                          >
                            <option value="">Select Type</option>
                            {PRODUCT_TYPES.map(type => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </Field>
                          {errors.type && touched.type && (
                            <div className="mt-1 text-sm text-red-600 flex items-center">
                              <AlertCircle className="w-4 h-4 mr-1" />
                              {errors.type}
                            </div>
                          )}
                        </div>

                        <div>
                          <label htmlFor="collectionType" className="block text-sm font-medium text-gray-700">
                            Collection Type *
                          </label>
                          <Field
                            as="select"
                            id="collectionType"
                            name="collectionType"
                            className={`mt-1 block w-full rounded-md shadow-sm ${
                              errors.collectionType && touched.collectionType
                                ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                            }`}
                          >
                            <option value="">Select Collection Type</option>
                            {COLLECTION_TYPES.map(type => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </Field>
                          {errors.collectionType && touched.collectionType && (
                            <div className="mt-1 text-sm text-red-600 flex items-center">
                              <AlertCircle className="w-4 h-4 mr-1" />
                              {errors.collectionType}
                            </div>
                          )}
                        </div>

                        <div>
                          <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                            Condition *
                          </label>
                          <Field
                            as="select"
                            id="category"
                            name="category"
                            className={`mt-1 block w-full rounded-md shadow-sm ${
                              errors.category && touched.category
                                ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                            }`}
                          >
                            <option value="">Select Condition</option>
                            {PRODUCT_CATEGORIES.map(category => (
                              <option key={category} value={category}>{category}</option>
                            ))}
                          </Field>
                          {errors.category && touched.category && (
                            <div className="mt-1 text-sm text-red-600 flex items-center">
                              <AlertCircle className="w-4 h-4 mr-1" />
                              {errors.category}
                            </div>
                          )}
                        </div>

                        <div>
                          <label htmlFor="price" className="block text-sm font-medium text-gray-700">
                            Price *
                          </label>
                          <Field
                            id="price"
                            name="price"
                            type="number"
                            step="0.01"
                            className={`mt-1 block w-full rounded-md shadow-sm ${
                              errors.price && touched.price
                                ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                            }`}
                          />
                          {errors.price && touched.price && (
                            <div className="mt-1 text-sm text-red-600 flex items-center">
                              <AlertCircle className="w-4 h-4 mr-1" />
                              {errors.price}
                            </div>
                          )}
                        </div>

                        <div>
                          <label htmlFor="stockLevel" className="block text-sm font-medium text-gray-700">
                            Stock Level *
                          </label>
                          <Field
                            id="stockLevel"
                            name="stockLevel"
                            type="number"
                            className={`mt-1 block w-full rounded-md shadow-sm ${
                              errors.stockLevel && touched.stockLevel
                                ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                            }`}
                          />
                          {errors.stockLevel && touched.stockLevel && (
                            <div className="mt-1 text-sm text-red-600 flex items-center">
                              <AlertCircle className="w-4 h-4 mr-1" />
                              {errors.stockLevel}
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                          Description *
                        </label>
                        <RichTextEditor
                          value={values.description}
                          onChange={(value) => setFieldValue('description', value)}
                          placeholder="Enter product description..."
                        />
                        {errors.description && touched.description && (
                          <div className="mt-1 text-sm text-red-600 flex items-center">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            {errors.description}
                          </div>
                        )}
                      </div>

                      {/* Server Error Display */}
                      {status && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-4">
                          <div className="flex">
                            <AlertCircle className="h-5 w-5 text-red-400" />
                            <div className="ml-3">
                              <h3 className="text-sm font-medium text-red-800">
                                Save Failed
                              </h3>
                              <div className="mt-2 text-sm text-red-700">
                                {status}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="flex justify-end space-x-3">
                        <button
                          type="button"
                          onClick={handleClose}
                          disabled={isSubmitting}
                          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400 disabled:cursor-not-allowed"
                        >
                          {isSubmitting ? 'Saving...' : 'Save Changes'}
                        </button>
                      </div>

                      {/* Server Route Indicator */}
                      <div className="text-center">
                        <p className="text-xs text-blue-600">
                          🔒 Using secure server route (bypasses RLS)
                        </p>
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <AlertDialog.Root open={showUnsavedChanges} onOpenChange={setShowUnsavedChanges}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          <AlertDialog.Content className="fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] w-[95vw] max-w-md bg-white rounded-lg shadow-lg p-6">
            <AlertDialog.Title className="text-lg font-semibold text-gray-900 mb-4">
              Unsaved Changes
            </AlertDialog.Title>
            <AlertDialog.Description className="text-sm text-gray-600 mb-6">
              You have unsaved changes. Are you sure you want to close without saving?
            </AlertDialog.Description>
            <div className="flex justify-end space-x-3">
              <AlertDialog.Cancel asChild>
                <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                  Cancel
                </button>
              </AlertDialog.Cancel>
              <AlertDialog.Action asChild>
                <button
                  onClick={() => {
                    setFormValues(null);
                    onClose();
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
                >
                  Discard Changes
                </button>
              </AlertDialog.Action>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </>
  );
}